import React, {useEffect, useId, useRef, useState} from 'react';
import {formatPlaybackTime, parsePlaybackTime} from './bunnyPlayback';

export default function BunnyMp4Player({sources, title, onFallback, initialSeconds=0, initialAutoplay=false}) {
    const video=useRef(null), position=useRef(initialSeconds), intent=useRef(initialAutoplay), pending=useRef(initialSeconds);
    const volume=useRef(1),muted=useRef(false);
    const [quality,setQuality]=useState('480'),[revision,setRevision]=useState(0);
    const [qualityNotice,setQualityNotice]=useState('');
    const [status,setStatus]=useState('loading'),[duration,setDuration]=useState(0),[speed,setSpeed]=useState(1);
    const [jumpTime,setJumpTime]=useState(''),[jumpError,setJumpError]=useState('');
    const [offline,setOffline]=useState(()=>navigator.onLine===false);
    const id=useId();
    const safelyPlay=()=>{
        intent.current=true;
        const element=video.current;
        try {element?.play()?.catch(()=>{
            // A failing rendition also rejects play(). Its stale promise must not
            // pause the replacement rendition or overwrite its playback status.
            if(video.current!==element || element.error)return;
            intent.current=false;setStatus('tap-play');
        });}
        catch {intent.current=false;setStatus('tap-play');}
    };
    const snapshot=()=>({seconds:position.current,autoplay:intent.current || Boolean(video.current && !video.current.paused && !video.current.ended)});
    const reload=(nextQuality=quality)=>{
        const state=snapshot();pending.current=state.seconds;intent.current=state.autoplay;
        setQuality(nextQuality);setRevision(n=>n+1);setStatus('loading');
    };
    const seek=(seconds,play=false)=>{
        const target=Math.max(0,duration>0?Math.min(seconds,duration-.1):seconds);
        position.current=target;pending.current=target;
        if(video.current?.readyState>=1){video.current.currentTime=target;pending.current=null;}
        setStatus('seeking');if(play)safelyPlay();
    };
    const jump=e=>{
        e.preventDefault();const seconds=parsePlaybackTime(jumpTime);
        if(seconds===null || (duration>0 && seconds>=duration)){setJumpError(duration>0?`Enter a time before ${formatPlaybackTime(duration)}.`:'Enter a time such as 1:30 or 0:00.');return;}
        setJumpError('');seek(seconds,true);
    };
    const loaded=()=>{
        const element=video.current;
        if(!element)return;
        if(Number.isFinite(element.duration) && element.duration>0)setDuration(element.duration);
        element.playbackRate=speed;
        const savedVolume=volume.current,savedMute=muted.current;
        element.volume=savedVolume;element.muted=savedMute;
        if(pending.current!==null){
            const end=Number.isFinite(element.duration)?Math.max(0,element.duration-.1):Infinity;
            const target=Math.min(pending.current,end);
            if(Math.abs(element.currentTime-target)>.05)element.currentTime=target;
            pending.current=null;
        }
        setStatus('ready');if(intent.current)safelyPlay();
    };
    const failed=()=>{
        // An unavailable rendition must not strand the student on a broken video.
        if(video.current?.error?.code===1)return;
        if(quality==='720'){setQualityNotice('720p could not play. Switched to 480p.');reload('480');}
        else onFallback(snapshot());
    };
    useEffect(()=>{
        const connection=()=>setOffline(navigator.onLine===false);
        window.addEventListener('online',connection);window.addEventListener('offline',connection);
        return ()=>{window.removeEventListener('online',connection);window.removeEventListener('offline',connection);};
    },[]);
    useEffect(()=>{
        if(offline || !['loading','seeking','buffering'].includes(status))return;
        const timeout=setTimeout(()=>setStatus('slow'),15000);
        return ()=>clearTimeout(timeout);
    },[status,offline,revision]);
    const messages={loading:'Loading your explanation…',ready:'Ready when you are. Press Play to watch.',playing:'Playing your tutor’s explanation.',paused:'Paused. Press Play to continue.',seeking:'Moving to the selected time…',buffering:'Loading the next part of the explanation…',slow:'This is taking longer than expected. Try 480p, Reload video, or Use streaming player.',ended:'Explanation complete. You can replay it anytime.','tap-play':'Press Play in the video to continue.'};
    return <section className="instil-video" aria-label={title}>
        <div className="instil-video-heading"><span aria-hidden="true">▷</span><strong>{title}</strong><span>Standard playback</span></div>
        <div className="instil-video-stage">
            <video key={`${quality}-${revision}`} ref={video} src={sources[quality]} title={title} aria-label={title}
                controls playsInline preload="metadata" controlsList="nodownload"
                onLoadedMetadata={loaded} onDurationChange={()=>{if(Number.isFinite(video.current?.duration) && video.current.duration>0)setDuration(video.current.duration);}}
                onTimeUpdate={()=>{if(pending.current===null && !video.current?.seeking)position.current=video.current.currentTime;}}
                onPlaying={()=>{intent.current=true;setStatus('playing');}}
                onPlay={()=>{intent.current=true;setStatus('playing');}}
                onPause={e=>{if(e.currentTarget===video.current && !e.currentTarget.ended && !e.currentTarget.error){intent.current=false;setStatus('paused');}}}
                onVolumeChange={()=>{volume.current=video.current.volume;muted.current=video.current.muted;}}
                onRateChange={()=>setSpeed(video.current.playbackRate)}
                onWaiting={()=>setStatus('buffering')} onSeeking={()=>{if(pending.current===null)position.current=video.current.currentTime;setStatus('seeking');}}
                onSeeked={()=>{position.current=video.current.currentTime;setStatus(video.current.paused?'paused':'playing');}}
                onEnded={()=>{position.current=0;intent.current=false;setStatus('ended');}}
                onError={failed}/>
        </div>
        <div className="instil-video-help">
            <p role="status">{offline?'You’re offline. Reconnect to the internet, then press Play.':messages[status]}</p>
            {qualityNotice && <p className="instil-video-quality-notice" aria-live="polite">{qualityNotice}</p>}
            <div className="instil-video-controls">
                <button type="button" disabled={!duration || offline} onClick={()=>seek((video.current?.currentTime || 0)-10)}>↶ 10 seconds</button>
                <button type="button" disabled={!duration || offline} onClick={()=>seek((video.current?.currentTime || 0)+10)}>10 seconds ↷</button>
                <label>Quality <select aria-label="Video quality" value={quality} disabled={offline} onChange={e=>{setQualityNotice('');reload(e.target.value);}}><option value="480">480p · Less data</option><option value="720">720p · Clear</option></select></label>
                <label>Speed <select aria-label="Playback speed" value={speed} onChange={e=>{const value=Number(e.target.value);setSpeed(value);if(video.current)video.current.playbackRate=value;}}>{[.75,1,1.25,1.5,2].map(rate=><option key={rate} value={rate}>{rate}×</option>)}</select></label>
            </div>
            <form className="instil-video-jump" onSubmit={jump}>
                <label htmlFor={id}>Jump to a time{duration>0 && <span> · {formatPlaybackTime(duration)} total</span>}</label>
                <div><input id={id} type="text" value={jumpTime} onChange={e=>{setJumpTime(e.target.value);setJumpError('');}} placeholder="e.g. 2:26" maxLength={12} aria-describedby={jumpError?`${id}-error`:undefined} aria-invalid={!!jumpError}/><button type="submit" disabled={offline}>Jump &amp; play</button></div>
                {jumpError && <p id={`${id}-error`} role="alert">{jumpError}</p>}
            </form>
            <div className="instil-video-alternatives"><button type="button" disabled={offline} onClick={()=>reload()}>Reload video</button><button type="button" disabled={offline} onClick={()=>onFallback(snapshot())}>Use streaming player</button></div>
            <small>Drag the video’s timeline to skip forward or back. Choose 480p on a slower connection.</small>
        </div>
    </section>;
}
